import { Unsubscribe } from 'firebase/firestore';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { db } from '../firebase';
import { Day, Event, Location, Assignment } from '../types';

/*
-------------------------------------------------------------------------------------
    Schedule Object Structure
-------------------------------------------------------------------------------------
    [
        {
            id: "[uid]"
            name: "Day 1",
            date: "8/20",
            events: [
                {
                    id: "[uid]",
                    name: "Opening Ceremony",
                    start: "8:00 am",
                    end: "9:00 am",
                    location: {
                        name: "Main Hall",
                        lat: 0,
                        lng: 0,
                    } as Location,
                    link: "https://zoom.com/123",
                    speaker: "Cole Harten",
                    speakerTitle: "CEO of Harten Industries",
                    assignedIDs: ["uid1", "uid2", ...],
                },
                ...
            ]
        },
        ...
    ]
*/

export function subscribeToSchedule(confCode: string, callback: (data: Day[]) => void): Unsubscribe {
    const scheduleRef = db.collection("conferences").doc(confCode).collection("days");
    const unsubscribe = scheduleRef.onSnapshot(async (snapshot) => {
        const schedule: Day[] = await Promise.all(snapshot.docs.map(async (doc) => {
            const dayData = doc.data();
            const eventsSnapshot = await db.collection(`conferences/${confCode}/days/${doc.id}/events`).get();
            const events: Event[] = eventsSnapshot.docs.map((eventDoc) => {
                const eventData = eventDoc.data();
                return {
                    id: eventDoc.id,
                    name: eventData.name,
                    start: eventData.start,
                    end: eventData.end,
                    location: eventData.location || null,
                    link: eventData.link || null,
                    speaker: eventData.speaker || null,
                    speakerTitle: eventData.speakerTitle || null,
                    assignedIDs: eventData.assignedIDs || [],
                    scannedIDs: eventData.scannedIDs || [],
                };
            })
            // Sort events by start time
            .sort((a, b) => {
                const padTime = (time: string) => {
                    let [hours, minutes] = time.split(':');
                    hours = hours.padStart(2, '0'); // Ensure two digits for hours
                    return `${hours}:${minutes}`;
                };
                return padTime(a.start).localeCompare(padTime(b.start));
            });

            return {
                id: doc.id,
                date: dayData.date,
                events: events || [],
            };
        }));   
        
        // Sort days by date
        const sortedSchedule = schedule.sort((a, b) => {
            if (!a.date || !b.date) return 0;

            const aDateParts = a.date.split("/");
            const bDateParts = b.date.split("/");
            const aMonth = parseInt(aDateParts[0]);
            const bMonth = parseInt(bDateParts[0]);
            const aDay = parseInt(aDateParts[1]);
            const bDay = parseInt(bDateParts[1]);

            // Compare months first
            if (aMonth !== bMonth) {
                return aMonth - bMonth;
            }

            // If months are the same, compare days
            return aDay - bDay;
        });

        callback(sortedSchedule);
    });
    return unsubscribe;
}

export async function updateDay(confCode: string, day: Day): Promise<void> {
    try {
        const dayRef = db.doc(`conferences/${confCode}/days/${day.id}`);
        
        // Start a batch to perform multiple operations
        const batch = db.batch();

        // Update the day document
        batch.set(dayRef, {
            date: day.date,
        });

        // Get the reference to the events collection
        const eventsRef = db.collection(`conferences/${confCode}/days/${day.id}/events`);

        // Fetch existing events
        const existingEventsSnapshot = await eventsRef.get();
        
        // Delete events not present in the updated day
        existingEventsSnapshot.forEach(doc => {
            if (!day.events.some(updatedEvent => updatedEvent.id === doc.id)) {
                batch.delete(doc.ref);
            }
        });

        // Update or add events in the updated day
        day.events.forEach(event => {
            const eventRef = eventsRef.doc(event.id);
            batch.set(eventRef, {
                name: event.name,
                start: event.start,
                end: event.end,
                location: event.location || null,
                link: event.link || null,
                speaker: event.speaker || null,
                speakerTitle: event.speakerTitle || null,
                assignedIDs: event.assignedIDs || [],
            });
        });

        // Commit the batch
        await batch.commit();
    } catch (error) {
        console.error("Error updating day:", error);
        throw error;
    }
}


export async function deleteDay(confCode: string, dayId: string): Promise<void> {
    try {
        const dayRef = db.doc(`conferences/${confCode}/days/${dayId}`);
        await dayRef.delete();
    }
    catch (error) {
        console.error("Error deleting day:", error);
        throw error;
    }
}

export async function getLocations(confCode: string): Promise<Location[]> {
    try {
        const daysRef = await db.collection(`conferences/${confCode}/days`).get();
        const locations: Location[] = [];

        for (const dayDoc of daysRef.docs) {
            const eventsRef = await db.collection(`conferences/${confCode}/days/${dayDoc.id}/events`).get();
            for (const eventDoc of eventsRef.docs) {
                const eventData = eventDoc.data();
                if (eventData.location) {
                    locations.push(eventData.location);
                }
            }
        }

        return locations;
    } catch (error) {
        console.error("Error getting locations:", error);
        throw error;
    }
}

// Clear all assignments from all events in a conference
export async function clearAllAssignments(confCode: string): Promise<void> {
    try {
        console.log('=== clearAllAssignments called ===');
        console.log('confCode:', confCode);
        console.log('Clearing all assignments from database...');
        
        const daysRef = db.collection(`conferences/${confCode}/days`);
        const daysSnapshot = await daysRef.get();

        if (daysSnapshot.empty) {
            throw new Error('No days found for this conference');
        }

        // Clear assignments from all events in the database
        for (const dayDoc of daysSnapshot.docs) {
            const eventsRef = db.collection(`conferences/${confCode}/days/${dayDoc.id}/events`);
            const eventsSnapshot = await eventsRef.get();
            
            for (const eventDoc of eventsSnapshot.docs) {
                await eventDoc.ref.update({
                    assignedIDs: []
                });
                console.log(`Cleared assignments for event ${eventDoc.id}`);
            }
        }
        
        console.log('All assignments cleared successfully');
    } catch (error) {
        console.error("Error clearing assignments:", error);
        throw error;
    }
}

// Get all events for a conference (for bulk assignment dialog)
export async function getAllEvents(confCode: string): Promise<Array<{ id: string; name: string }>> {
    try {
        const daysRef = db.collection(`conferences/${confCode}/days`);
        const daysSnapshot = await daysRef.get();

        if (daysSnapshot.empty) {
            throw new Error('No days found for this conference');
        }

        const allEvents: Array<{ id: string; name: string }> = [];
        
        for (const dayDoc of daysSnapshot.docs) {
            const eventsRef = db.collection(`conferences/${confCode}/days/${dayDoc.id}/events`);
            const eventsSnapshot = await eventsRef.get();
            
            eventsSnapshot.docs.forEach(doc => {
                const eventData = doc.data();
                allEvents.push({
                    id: doc.id,
                    name: eventData.name
                });
            });
        }

        return allEvents;
    } catch (error) {
        console.error("Error getting events:", error);
        throw error;
    }
}

// Get all events with their paths for a conference
export async function getAllEventsWithPaths(confCode: string): Promise<Array<Event & { path: string; dayId: string }>> {
    try {
        const daysRef = db.collection(`conferences/${confCode}/days`);
        const daysSnapshot = await daysRef.get();

        if (daysSnapshot.empty) {
            throw new Error('No days found for this conference');
        }

        const allEvents: Array<Event & { path: string; dayId: string }> = [];
        
        for (const dayDoc of daysSnapshot.docs) {
            const eventsRef = db.collection(`conferences/${confCode}/days/${dayDoc.id}/events`);
            const eventsSnapshot = await eventsRef.get();
            
            eventsSnapshot.docs.forEach(doc => {
                const eventData = doc.data();
                allEvents.push({
                    id: doc.id,
                    name: eventData.name,
                    start: eventData.start,
                    end: eventData.end,
                    location: eventData.location || null,
                    link: eventData.link || null,
                    speaker: eventData.speaker || null,
                    speakerTitle: eventData.speakerTitle || null,
                    assignedIDs: eventData.assignedIDs || [],
                    path: `conferences/${confCode}/days/${dayDoc.id}/events/${doc.id}`,
                    dayId: dayDoc.id
                });
            });
        }

        return allEvents;
    } catch (error) {
        console.error("Error getting events with paths:", error);
        throw error;
    }
}

// Assign users to events in bulk
export async function assignUsersToEvents(confCode: string, assignments: Assignment[]): Promise<void> {
    try {
        console.log('=== assignUsersToEvents called ===');
        console.log('confCode:', confCode);
        console.log('assignments:', assignments);
        
        if (!assignments || assignments.length === 0) {
            throw new Error('No assignments provided');
        }

        console.log('Starting assignments with:', {
            conferenceCode: confCode,
            assignments: assignments.map(a => ({
                email: a.email,
                events: a.eventIds
            }))
        });

        // Get all events with their paths
        const allEvents = await getAllEventsWithPaths(confCode);
        
        // Build a map of event IDs to their full paths and day IDs
        const eventDayMap = new Map<string, { path: string; dayId: string }>();
        allEvents.forEach(event => {
            eventDayMap.set(event.id, {
                path: event.path,
                dayId: event.dayId
            });
        });

        // Verify all events exist
        for (const assignment of assignments) {
            for (const eventId of assignment.eventIds) {
                if (!eventDayMap.has(eventId)) {
                    throw new Error(`Event ${eventId} not found in conference ${confCode}`);
                }
                console.log(`Verified event exists: ${eventId}`);
            }
        }

        // Create a map to track which users need to be added to which events
        const userEventMap = new Map<string, Set<string>>();
        
        // Process assignments and find users
        for (const assignment of assignments) {
            console.log(`Processing assignment for email: ${assignment.email}`);
            
            // Find user
            const userQuery = await db
                .collection('users')
                .where('email', '==', assignment.email.toLowerCase())
                .get();

            if (userQuery.empty) {
                throw new Error(`User not found: ${assignment.email}`);
            }

            const uid = userQuery.docs[0].id;
            console.log(`Found user ${uid} for email ${assignment.email}`);

            // Track which events this user needs to be added to
            if (!userEventMap.has(uid)) {
                userEventMap.set(uid, new Set());
            }
            
            // Add all event IDs to the user's set
            assignment.eventIds.forEach((eventId: string) => {
                userEventMap.get(uid)?.add(eventId);
            });
        }

        // Update each event with all its assigned users
        for (const [eventId, eventInfo] of eventDayMap.entries()) {
            // Find all users that need to be added to this event
            const usersToAdd = Array.from(userEventMap.entries())
                .filter(([_, eventIds]) => eventIds.has(eventId))
                .map(([uid]) => uid);
            
            if (usersToAdd.length > 0) {
                console.log(`Updating event ${eventId} with ${usersToAdd.length} users`);
                
                const eventRef = db.doc(eventInfo.path);
                
                try {
                    // Update the database
                    await eventRef.update({
                        assignedIDs: firebase.firestore.FieldValue.arrayUnion(...usersToAdd)
                    });
                    console.log(`Successfully updated event ${eventId} in database`);
                } catch (updateError: any) {
                    console.error('Update failed:', {
                        eventId,
                        error: updateError.message,
                        code: updateError.code
                    });
                    throw new Error(`Failed to update event ${eventId}: ${updateError.message}`);
                }
            }
        }
        
        console.log('All assignments completed successfully');

    } catch (error: any) {
        console.error('Assignment error:', {
            message: error.message,
            code: error.code,
            details: error
        });
        throw error;
    }
}
